// Claude: Merged Home + Login — role radio, side-by-side on desktop, constrained on mobile 05-03-2026
// Claude: active session warning modal + SESSION_TERMINATED graceful exit 18-04-2026
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Image,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Text,
  RadioGroup,
  Radio,
  HStack,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  ModalFooter,
} from "@chakra-ui/react";
import React, { useEffect, useRef, useState } from "react";
import { AiFillEye, AiFillEyeInvisible } from "react-icons/ai";
import { Icons } from "../../Assets/icons";
import { useNavigate } from "react-router-dom";
import { AuthAPI } from "../../Apis/authAPI";
import Loader from "../../Components/Loader/Loader";
import { useDispatch, useSelector } from "react-redux";
import actions from "../../redux/Actions";
import { useToastWrapper } from "../../Wrapper/toastWrapper";
import aftrlogo from "../../Assets/images/AftrBlueLogo2048.png";
import meetingstock from "../../Assets/images/meetingstock_1.jpg";
import { errorHandler } from "../../Utils/handler";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";

const Login = () => {
  const navigate = useNavigate();
  const user = useSelector((state: any) => state.user);
  const [role, setRole] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const { error } = useToastWrapper();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const submitRef = useRef<any>(null);
  // Claude: reCAPTCHA V3 — silent token execution on login submit 24-03-2026
  const { executeRecaptcha } = useGoogleReCaptcha();
  // Claude: active session warning modal state 18-04-2026
  const [activeSessionModal, setActiveSessionModal] = useState(false);
  const [activeSessionInfo, setActiveSessionInfo] = useState<any>(null);

  useEffect(() => {
    if (user && user?.token && user?.role && user?.user) {
      if (user.role === "Buyer") return navigate("/buyer/rfxs");
      if (user.role === "Seller") return navigate("/seller/rfxs");
      if (user.role === "Event_Organizer") return navigate("/organizer");
    }
    dispatch(actions.user.logout());
  }, []);


  useEffect(() => {
    const listener = (event: any) => {
      if (event.code === "Enter" || event.code === "NumpadEnter") {
        event.preventDefault();
        submitRef.current?.click();
      }
    };
    document.addEventListener("keydown", listener);
    return () => document.removeEventListener("keydown", listener);
  }, []);

  // Claude: shared login executor — used by normal login and force login 18-04-2026
  const executeLogin = async (forceLogin = false) => {
    if (!executeRecaptcha) return error("reCAPTCHA not ready. Please try again.", "");
    let recaptchaToken = "";
    try {
      recaptchaToken = await executeRecaptcha("login");
    } catch {
      return error("reCAPTCHA failed. Please refresh and try again.", "");
    }
    try {
      setLoading(true);
      const response = await AuthAPI.postLogin({ email, password, role, recaptchaToken, forceLogin });
      if (response) {
        const data = {
          token: response.data.token,
          user: response.data.data._id,
          role: response.data.data.role,
          verificationStatus: response.data.data.verificationStatus ?? null,
          verificationDeadline: response.data.data.verificationDeadline ?? null,
          // Claude: store display name for AI chat greeting 11-03-2026
          name: response.data.data.personName ?? null,
        };
        dispatch(actions.user.set(data));
        if (response.data.data.role === "Buyer") navigate("/buyer/rfxs");
        if (response.data.data.role === "Seller") navigate("/seller/rfxs");
        if (response.data.data.role === "Event_Organizer") navigate("/organizer");
      }
    } catch (err: any) {
      // Claude: ACTIVE_SESSION_EXISTS — show modal asking Person 2 to confirm kick 18-04-2026
      if (err?.response?.status === 409 && err?.response?.data?.code === "ACTIVE_SESSION_EXISTS") {
        setActiveSessionInfo(err.response.data.activeSession);
        setActiveSessionModal(true);
        return;
      }
      error(errorHandler(err), "");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!role) return error("Please select whether you are a Buyer or a Seller", "");
    if (!email || !password) return error("Please fill all the fields", "");
    await executeLogin(false);
  };

  const handleForceLogin = async () => {
    setActiveSessionModal(false);
    await executeLogin(true);
  };

  // Claude: dynamic overlay text based on role selection 16-05-2026
  const overlayTextMap: Record<string, { title: string; points: string[] } | null> = {
    "": null,
    Buyer: {
      title: "For Clients / B2B Buyers",
      points: [
        "Find solutions from verified solution providers matched to your exact business requirements.",
        "Post your requirement once and receive relevant solution responses",
        "Save time, reduce evaluation effort - no endless vendor hunting or cold outreach.",
      ],
    },
    Seller: {
      title: "For Solution Providers",
      points: [
        "Pitch your solutions to verified buyers at the exact moment they need them.",
        "Stay visible to prospects by showcasing your digital assets in buyer's feeds.",
        "Focus on high-intent opportunities instead of cold prospecting.",
      ],
    },
  };

  const renderOverlay = (isMobile: boolean) => {
    const content = overlayTextMap[role];
    return (
      <Flex position="absolute" inset={0} align="center" justify="center" bg="blackAlpha.500">
        {content ? (
          <Flex direction="column" align="flex-start" w="100%" px={isMobile ? 3 : 6} gap={isMobile ? 4 : 6}
            bg="blackAlpha.400" borderRadius="16px" py={isMobile ? 4 : 6} mx={isMobile ? 3 : 6}
          >
            <Text
              fontSize={isMobile ? "md" : "2xl"} fontWeight={700} textAlign="center" w="100%"
              textDecoration="underline"
              color="#00FF41" textShadow="0 2px 12px rgba(0,0,0,0.7)" mb={isMobile ? 1 : 2}
            >
              {content.title}
            </Text>
            {content.points.map((point, i) => (
              <Text
                key={i}
                fontSize={isMobile ? "xs" : "lg"} fontWeight={700} textAlign="left"
                color="#00FF41" textShadow="0 2px 12px rgba(0,0,0,0.7)" lineHeight="1.5"
              >
                {point}
              </Text>
            ))}
          </Flex>
        ) : (
          <Text
            fontSize={isMobile ? "lg" : "3xl"} fontWeight={700} textAlign="center"
            px={isMobile ? 6 : 8} color="#00FF41"
            textShadow={isMobile ? "0 2px 8px rgba(0,0,0,0.7)" : "0 2px 12px rgba(0,0,0,0.7)"}
          >
            Connecting Enterprises with B2B Solution providers
          </Text>
        )}
      </Flex>
    );
  };

  const formSection = (
    <Box px={6} pt={1} pb={3} display="flex" flexDirection="column" justifyContent="center" h="100%">
      {loading ? <Loader /> : null}

      {/* AFTR Logo */}
      {/* Claude: added BETA fine print below logo 19-03-2026 */}
      <Flex justifyContent="center" alignItems="center" direction="column" mb={7}>
        <Box overflow="hidden" h="62px" w="80px">
          <Image src={aftrlogo} alt="Aftr.Network" w="80px" h="80px" objectFit="contain" mt="-16px" />
        </Box>
        <Text fontSize="9px" fontWeight={300} color="white" letterSpacing="widest" mt="-18px">BETA</Text>
      </Flex>

      {/* Role radio */}
      <Flex justifyContent="center" mb={3}>
        <RadioGroup value={role} onChange={setRole}>
          <HStack spacing={8}>
            <Radio value="Buyer" colorScheme="yellow" size="lg" borderColor="whiteAlpha.600">
              <Text color="white" fontSize="sm" fontWeight={600}>Enterprise Client / B2B Buyer</Text>
            </Radio>
            <Radio value="Seller" colorScheme="yellow" size="lg" borderColor="whiteAlpha.600">
              <Text color="white" fontSize="sm" fontWeight={600}>B2B Services / Solution Provider / Seller</Text>
            </Radio>
          </HStack>
        </RadioGroup>
      </Flex>

      {/* Email */}
      <FormControl>
        <FormLabel color="whiteAlpha.800" fontSize="sm">Email</FormLabel>
        <InputGroup>
          <InputLeftElement h="50px" pointerEvents="none">
            <Image src={Icons.LoginMail} alt="" />
          </InputLeftElement>
          <Input
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            h="50px"
            type="email"
            placeholder="Enter Email"
            bg="whiteAlpha.100"
            color="white"
            borderColor="whiteAlpha.400"
            _placeholder={{ color: "whiteAlpha.500" }}
            _focus={{ borderColor: "yellow.300", boxShadow: "none" }}
          />
        </InputGroup>
      </FormControl>

      {/* Password — Forgot Password on same line as label */}
      <FormControl mt={3}>
        <Flex justifyContent="space-between" alignItems="center" mb={1}>
          <FormLabel color="whiteAlpha.800" fontSize="sm" mb={0}>Password</FormLabel>
          <Text color="yellow.300" fontSize="xs" cursor="pointer" onClick={() => navigate("/forgot-password")}>
            Forgot Password?
          </Text>
        </Flex>
        <InputGroup>
          <InputLeftElement h="50px" pointerEvents="none">
            <Image src={Icons.LoginPassword} alt="" />
          </InputLeftElement>
          <Input
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            h="50px"
            type={showPassword ? "text" : "password"}
            placeholder="Enter Password"
            bg="whiteAlpha.100"
            color="white"
            borderColor="whiteAlpha.400"
            _placeholder={{ color: "whiteAlpha.500" }}
            _focus={{ borderColor: "yellow.300", boxShadow: "none" }}
          />
          <InputRightElement h="50px">
            {showPassword ? (
              <AiFillEyeInvisible color="white" style={{ cursor: "pointer" }} onClick={() => setShowPassword(false)} />
            ) : (
              <AiFillEye color="white" style={{ cursor: "pointer" }} onClick={() => setShowPassword(true)} />
            )}
          </InputRightElement>
        </InputGroup>
      </FormControl>

      {/* Terms */}
      <Text color="whiteAlpha.700" fontSize="sm" mt={3} textAlign="center">
        I accept these{" "}
        <Text
          as="span"
          cursor="pointer"
          color="yellow.300"
          onClick={() => window.open("https://prontocommerce.com/aftr/terms-of-use/", "_blank")}
        >
          terms and conditions
        </Text>
      </Text>

      {/* Sign In */}
      <Flex justifyContent="center" mt={3}>
        <Button
          ref={submitRef}
          onClick={handleSubmit}
          w="50%"
          h="42px"
          borderRadius="10px"
          background="secondary.main"
          color="white"
          _hover={{ opacity: 0.85 }}
        >
          Sign In
        </Button>
      </Flex>

      {/* Sign Up link */}
      <Text mt={6} fontSize="sm" color="whiteAlpha.600" textAlign="center">
        New User?{" "}
        <Text as="span" color="yellow.300" textDecoration="underline" cursor="pointer" onClick={() => navigate("/register")}>
          Sign Up / Register
        </Text>
      </Text>

      <Text mt={10} fontSize="sm" color="whiteAlpha.400" textAlign="center">
        Email{" "}
        <Text as="a" href="mailto:support@aftr.network" color="whiteAlpha.600" _hover={{ color: "yellow.300" }}>
          support@aftr.network
        </Text>
        {" "}for any inquiries.
      </Text>
    </Box>
  );

  return (
    <Box minH="100dvh" bg="primary.blue">

      {/* Claude: ACTIVE_SESSION_EXISTS modal — Person 2 must confirm before kicking Person 1 18-04-2026 */}
      <Modal isOpen={activeSessionModal} onClose={() => setActiveSessionModal(false)} isCentered>
        <ModalOverlay />
        <ModalContent bg="#1a2040" borderRadius="12px" mx={4}>
          <ModalBody pt={6} pb={2}>
            <Text color="white" fontWeight={700} fontSize="md" mb={3}>
              Simultaneous Logins Detected
            </Text>
            <Text color="whiteAlpha.800" fontSize="sm" mb={4}>
              This account is currently active in another session. If you continue, that session will be ended.
            </Text>
            <Text color="whiteAlpha.700" fontSize="sm">
              Do you want to proceed?
            </Text>
          </ModalBody>
          <ModalFooter gap={3}>
            <Button
              size="sm" variant="ghost" color="whiteAlpha.600"
              _hover={{ color: "white" }}
              onClick={() => setActiveSessionModal(false)}
            >
              Cancel
            </Button>
            <Button
              size="sm" bg="red.600" color="white"
              _hover={{ opacity: 0.85 }}
              onClick={handleForceLogin}
            >
              End Other Session & Login
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      {/* Desktop: side-by-side */}
      <Flex display={{ base: "none", md: "flex" }} h="100dvh">
        {/* Claude: image overlay text — dynamic per role 16-05-2026 */}
        <Box w="60%" flexShrink={0} overflow="hidden" position="relative">
          <Image src={meetingstock} w="100%" h="100%" objectFit="cover" objectPosition="center" />
          {renderOverlay(false)}
        </Box>
        <Box w="40%" overflowY="auto">
          {formSection}
        </Box>
      </Flex>

      {/* Mobile: constrained width, stacked */}
      <Box display={{ base: "block", md: "none" }} maxW="600px" mx="auto">
        <Box position="relative">
          <Image src={meetingstock} width="100%" display="block" />
          {renderOverlay(true)}
        </Box>
        {formSection}
      </Box>
    </Box>
  );
};

export default Login;
